import { Module, ActionContext } from 'vuex'
import { authApi, AuthUser, LoginPayload } from '@/api/auth'

export interface AuthState {
  user: AuthUser | null
  accessToken: string | null
  refreshToken: string | null
}

type AuthContext = ActionContext<AuthState, unknown>

const auth: Module<AuthState, unknown> = {
  namespaced: true,

  state: (): AuthState => ({
    user: (() => {
      const raw = localStorage.getItem('auth_user')
      try { return raw ? JSON.parse(raw) : null } catch { return null }
    })(),
    accessToken: localStorage.getItem('access_token'),
    refreshToken: localStorage.getItem('refresh_token')
  }),

  getters: {
    isAuthenticated: (state) => !!state.accessToken && !!state.user,
    user: (state) => state.user,
    isAdmin: (state) => state.user?.role === 'admin',
    isAgent: (state) => state.user?.role === 'agent',
    isActive: (state) => state.user?.status === 'active'
  },

  mutations: {
    SET_AUTH (state, payload: { user: AuthUser; accessToken: string; refreshToken: string }) {
      state.user = payload.user
      state.accessToken = payload.accessToken
      state.refreshToken = payload.refreshToken
      localStorage.setItem('access_token', payload.accessToken)
      localStorage.setItem('refresh_token', payload.refreshToken)
      localStorage.setItem('auth_user', JSON.stringify(payload.user))
    },
    SET_ACCESS_TOKEN (state, token: string) {
      state.accessToken = token
      localStorage.setItem('access_token', token)
    },
    CLEAR_AUTH (state) {
      state.user = null
      state.accessToken = null
      state.refreshToken = null
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('auth_user')
    }
  },

  actions: {
    async login ({ commit }: AuthContext, payload: LoginPayload) {
      const { data } = await authApi.login(payload)
      commit('SET_AUTH', {
        user: data.user,
        accessToken: data.access_token,
        refreshToken: data.refresh_token
      })
      return data.user
    },

    async logout ({ commit, state }: AuthContext) {
      if (state.user?.role) {
        try {
          await authApi.logout(state.user.role)
        } catch {
          // Ignore — still clear local state
        }
      }
      commit('CLEAR_AUTH')
    },

    async refreshToken ({ commit, state }: AuthContext) {
      if (!state.refreshToken) throw new Error('No refresh token')
      const { data } = await authApi.refresh(state.refreshToken)
      commit('SET_ACCESS_TOKEN', data.access_token)
    }
  }
}

export default auth
